// Astryx themes for the studio site. «classic» is dark with the studio's accent; «bright» is white
// with a saturated version of it on the hero, buttons and selections. The accent is per studio
// (from the DB), so themes are created at runtime with defineTheme — documented for dynamic branding.
import { defineTheme } from '@astryxdesign/core/theme';
import { themes, defaultThemeSlug } from '../astryx-themes';

const base = themes[defaultThemeSlug];
const cache = new Map<string, ReturnType<typeof defineTheme>>();

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function readableOn(hex: string): string {
  return luminance(hex) > 0.35 ? '#140F12' : '#FFFFFF';
}

function toHsl(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (!d) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}

function hsl(h: number, s: number, l: number): string {
  const f = (k: number) => {
    const n = (k + h / 30) % 12;
    return Math.round(255 * (l - s * Math.min(l, 1 - l) * Math.max(-1, Math.min(n - 3, 9 - n, 1))));
  };
  return `#${[f(0), f(8), f(4)].map((v) => v.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

export type StudioDesign = 'classic' | 'bright';

const typography = {
  scale: { base: 15, ratio: 1.2 },
  body: { family: 'Manrope Variable', fallbacks: "Manrope, -apple-system, 'Segoe UI', Roboto, sans-serif" },
  heading: { family: 'Manrope Variable', fallbacks: "Manrope, -apple-system, 'Segoe UI', sans-serif" },
};

export interface BrightPalette {
  /** Saturated studio colour: hero, buttons, selections; white text stays legible on it. */
  accent: string;
  /** Tint for chips, inputs and cards. */
  soft: string;
  line: string;
  /** Page background around the app on wide screens. */
  board: string;
  ink: string;
  muted: string;
}

/**
 * The bright palette keeps the hue of the studio colour. Studios store pastel accents for the dark
 * design (#F2A7C3); white text needs a saturated one, so a pastel is deepened until it reaches 3.2:1
 * (bold 16px+ text). A colour that already passes is used as is.
 */
export function brightPalette(seed: string): BrightPalette {
  const [h, s] = toHsl(seed);
  const onWhite = (hex: string) => 1.05 / (luminance(hex) + 0.05);
  let accent = seed.toUpperCase();
  if (s < 0.6 || onWhite(accent) < 3.2) {
    let l = 0.62;
    accent = hsl(h, 0.86, l);
    while (onWhite(accent) < 3.2 && l > 0.3) accent = hsl(h, 0.86, (l -= 0.01));
  }
  return { accent, soft: hsl(h, 1, 0.97), line: hsl(h, 0.55, 0.92), board: hsl(h, 1, 0.94), ink: hsl(h, 0.25, 0.095), muted: hsl(h, 0.06, 0.53) };
}

export function studioTheme(accent = '#E8A598', design: StudioDesign = 'classic') {
  return design === 'bright' ? brightTheme(accent) : classicTheme(accent);
}

function brightTheme(seed: string) {
  const key = `bright-${seed.toUpperCase()}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const p = brightPalette(seed);
  const onest = { family: 'Onest Variable', fallbacks: "Onest, -apple-system, 'Segoe UI', Roboto, sans-serif" };
  const theme = defineTheme({
    name: `studio-${key.toLowerCase().replace('#', '')}`,
    extends: base,
    color: { accent: [p.accent, p.accent] },
    typography: { scale: typography.scale, body: onest, heading: onest },
    radius: { base: 4, multiplier: 2 },
    tokens: {
      // The seed alone gives a shifted accent (the palette adds chroma); the computed colour is set as is.
      '--color-accent': [p.accent, p.accent],
      '--color-on-accent': ['#FFFFFF', '#FFFFFF'],
      '--color-background-body': ['#FFFFFF', '#FFFFFF'],
      '--color-background-surface': ['#FFFFFF', '#FFFFFF'],
      '--color-background-card': ['#FFFFFF', '#FFFFFF'],
      '--color-background-popover': ['#FFFFFF', '#FFFFFF'],
      '--color-background-muted': [p.soft, p.soft],
      '--color-border': [p.line, p.line],
      '--color-border-emphasized': [p.muted, p.muted],
      '--color-text-primary': [p.ink, p.ink],
      '--color-text-secondary': [p.muted, p.muted],
    },
  });
  cache.set(key, theme);
  return theme;
}

function classicTheme(accent: string) {
  const key = accent.toUpperCase();
  const hit = cache.get(key);
  if (hit) return hit;
  const theme = defineTheme({
    name: `studio-${key.slice(1).toLowerCase()}`,
    extends: base,
    color: { accent: [key, key], neutralStyle: 'warm' },
    typography,
    radius: { base: 4, multiplier: 1.5 },
    tokens: {
      '--color-background-body': ['#0B0A0C', '#0B0A0C'],
      '--color-background-surface': ['#141215', '#141215'],
      '--color-background-card': ['#161417', '#161417'],
      '--color-background-popover': ['#1C191D', '#1C191D'],
      '--color-background-muted': ['#1E1B1F', '#1E1B1F'],
      '--color-border': ['rgba(255,255,255,0.08)', 'rgba(255,255,255,0.08)'],
      '--color-border-emphasized': ['rgba(255,255,255,0.16)', 'rgba(255,255,255,0.16)'],
      '--color-text-primary': ['#F5EFF1', '#F5EFF1'],
      '--color-text-secondary': ['#A9A0A6', '#A9A0A6'],
      '--color-on-accent': [readableOn(key), readableOn(key)],
    },
  });
  cache.set(key, theme);
  return theme;
}
